/**
 * Browser Settings Playground Defaults
 * Frontend-only configuration for tweaking image filtering thresholds
 */

export type NsfwPresetLevel = "light" | "medium" | "strict";
export type AccuracyLevel = 0 | 1 | 2; // 0=accurate, 1=balanced, 2=fast

export interface TextFilterCategory {
  enabled: boolean;
  redMark: number;
  urlRedMark: number;
  alwaysOn?: boolean;
}

export interface NsfwThresholds {
  porn: number;
  sexy: number;
  hentai: number;
  neutral: number;
  drawings: number;
  pornSexyCombo: number;
  hentaiSexyCombo: number;
}

export interface LevelSettings {
  nsfwLevel: NsfwPresetLevel;
  blockVideos: boolean;
  skinBlock: boolean;
  human: boolean;
  accuracy: AccuracyLevel;
  thresholds: NsfwThresholds;
  textFilter: Record<string, TextFilterCategory>;
}

export interface SharedPolicySettings {
  categories: string[];
  allowlist: string[];
  blocklist: string[];
}

export interface PlaygroundState {
  shared: SharedPolicySettings;
  levels: {
    light: LevelSettings;
    medium: LevelSettings;
    strict: LevelSettings;
  };
}

// Text filter category names
export const TEXT_FILTER_CATEGORIES = [
  "Adult",
  "AdultsVeryStrict",
  "Gambling",
  "Celebrities",
  "Dating",
  "Swimsuite",
] as const;

// Accuracy level labels
export const ACCURACY_LABELS: Record<AccuracyLevel, string> = {
  0: "Accurate",
  1: "Balanced",
  2: "Fast",
};

// Default text filter settings
const defaultTextFilter: Record<string, TextFilterCategory> = {
  Adult: { enabled: true, redMark: 3.0, urlRedMark: 2.0, alwaysOn: true },
  AdultsVeryStrict: { enabled: true, redMark: 2.0, urlRedMark: 1.0, alwaysOn: true },
  Gambling: { enabled: false, redMark: 5.0, urlRedMark: 3.0 },
  Celebrities: { enabled: false, redMark: 4.0, urlRedMark: 2.5 },
  Dating: { enabled: false, redMark: 3.0, urlRedMark: 2.0 },
  Swimsuite: { enabled: false, redMark: 3.0, urlRedMark: 2.0 },
};

// Default level settings
export const DEFAULT_LIGHT_SETTINGS: LevelSettings = {
  nsfwLevel: "light",
  blockVideos: false,
  skinBlock: false,
  human: false,
  accuracy: 2,
  thresholds: {
    porn: 0.75,
    sexy: 1.0,  // disabled
    hentai: 0.75,
    neutral: 0.0,  // disabled
    drawings: 1.0,  // disabled
    pornSexyCombo: 0.35,
    hentaiSexyCombo: 0.35,
  },
  textFilter: { ...defaultTextFilter },
};

export const DEFAULT_MEDIUM_SETTINGS: LevelSettings = {
  nsfwLevel: "medium",
  blockVideos: false,
  skinBlock: false,
  human: false,
  accuracy: 2,
  thresholds: {
    porn: 0.60,
    sexy: 0.60,
    hentai: 0.60,
    neutral: 0.0,  // disabled
    drawings: 1.0,  // disabled
    pornSexyCombo: 0.25,
    hentaiSexyCombo: 1.0,  // disabled
  },
  textFilter: { ...defaultTextFilter },
};

export const DEFAULT_STRICT_SETTINGS: LevelSettings = {
  nsfwLevel: "strict",
  blockVideos: false,
  skinBlock: false,
  human: false,
  accuracy: 2,
  thresholds: {
    porn: 0.40,
    sexy: 0.30,
    hentai: 0.40,
    neutral: 0.70,
    drawings: 1.0,  // disabled
    pornSexyCombo: 0.20,
    hentaiSexyCombo: 0.20,
  },
  textFilter: { ...defaultTextFilter },
};

export const DEFAULT_SHARED_SETTINGS: SharedPolicySettings = {
  categories: [],
  allowlist: [],
  blocklist: [],
};

export const getDefaultPlaygroundState = (): PlaygroundState => ({
  shared: { ...DEFAULT_SHARED_SETTINGS },
  levels: {
    light: JSON.parse(JSON.stringify(DEFAULT_LIGHT_SETTINGS)),
    medium: JSON.parse(JSON.stringify(DEFAULT_MEDIUM_SETTINGS)),
    strict: JSON.parse(JSON.stringify(DEFAULT_STRICT_SETTINGS)),
  },
});

// Generate effective rule text from thresholds
export function getEffectiveRuleText(thresholds: NsfwThresholds): string[] {
  const rules: string[] = [];
  
  if (thresholds.porn < 1.0) {
    rules.push(`Block if Porn ≥ ${(thresholds.porn * 100).toFixed(0)}%`);
  }
  if (thresholds.hentai < 1.0) {
    rules.push(`Block if Hentai ≥ ${(thresholds.hentai * 100).toFixed(0)}%`);
  }
  if (thresholds.sexy < 1.0) {
    rules.push(`Block if Sexy ≥ ${(thresholds.sexy * 100).toFixed(0)}%`);
  }
  if (thresholds.neutral < 1.0) {
    rules.push(`Block if Neutral ≤ ${(thresholds.neutral * 100).toFixed(0)}%`);
  }
  
  if (thresholds.pornSexyCombo < 1.0) {
    rules.push(`Block if P ≥ ${(thresholds.pornSexyCombo * 100).toFixed(0)}% AND S ≥ ${(thresholds.pornSexyCombo * 100).toFixed(0)}%`);
  }
  if (thresholds.hentaiSexyCombo < 1.0) {
    rules.push(`Block if H ≥ ${(thresholds.hentaiSexyCombo * 100).toFixed(0)}% AND S ≥ ${(thresholds.hentaiSexyCombo * 100).toFixed(0)}%`);
  }
  
  return rules;
}

// Generate JSON export for a level - matches backend format
export function generateLevelJson(shared: SharedPolicySettings, level: LevelSettings) {
  return {
    categories: shared.categories,
    allowlist: shared.allowlist,
    blocklist: shared.blocklist,
    config: {
      nsfwLevel: level.thresholds, // Raw PSHND threshold values
      human: level.human,
      accuracy: level.accuracy,
      // textFilter will be added by generateFullJson in BrowserSettingsSection
    },
  };
}
